import WidgetsIcon from '@mui/icons-material/Widgets';
import { Box, Stack } from '@mui/material';
import { useCreditLineAssignations } from 'lib/api/creditLine';
import { CreditLineAssignationsResponse } from 'lib/api/creditLine/types';
import { formatMoney } from 'lib/functions/utils';
import {
  BackBubbleButton,
  ContentCard,
  DocumentItem,
  ErrorState,
  HeroHeader,
  KeyValueCard,
  MobileTabs,
  TabContentSkeleton,
} from 'lib/mobileDesignSystem';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

type SubProduct = CreditLineAssignationsResponse['subProductAssignations'][0];

enum Tab {
  Deudores = 'deudores',
  Subproductos = 'subproductos',
}

const TABS = [
  { value: Tab.Subproductos, label: 'Subproductos' },
  { value: Tab.Deudores, label: 'Deudor' },
];

function nonNullRows<T>(
  entries: { label: string; value: T | null; format?: (v: T) => string }[],
): { label: string; value: string }[] {
  return entries
    .filter((e) => e.value != null)
    .map((e) => ({
      label: e.label,
      value: e.format ? e.format(e.value as T) : String(e.value),
    }));
}

const SubProductCard: React.FC<{ sub: SubProduct }> = ({ sub }) => {
  const items = nonNullRows([
    { label: 'Anticipo', value: sub.advance, format: (v) => `${v}%` },
    { label: 'Spread', value: sub.spread, format: (v) => `${v}%` },
    { label: 'Mora', value: sub.arrearRate, format: (v) => `${v}%` },
    {
      label: 'Días máx.',
      value: sub.maxFinancingDays,
      format: (v) => `${v} días`,
    },
    {
      label: 'Monto doc. máx.',
      value: sub.maxDocumentAmount,
      format: (v) => `${formatMoney(v)}`,
    },
    {
      label: 'Componente fijo',
      value: sub.operationCostFixed,
      format: (v) => `${formatMoney(v)}`,
    },
    {
      label: 'Componente variable',
      value: sub.operationCostVariable,
      format: (v) => `${v}%`,
    },
    {
      label: 'Tasa desc. fija',
      value: sub.discountRateFixed,
      format: (v) => `${v}%`,
    },
    {
      label: 'Tasa desc. variable',
      value: sub.discountRateVariable,
      format: (v) => `${v}%`,
    },
  ]);

  return (
    <ContentCard
      headerWithBackground={false}
      header={{
        title: sub.name,
        description: `${formatMoney(sub.amount)} CLP`,
        icon: <WidgetsIcon sx={{ fontSize: 20 }} />,
      }}
    >
      {items.length > 0 && <KeyValueCard items={items} highlightLastRows={0} />}
    </ContentCard>
  );
};

const SubProducts: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requestId = id != null && id !== '' ? Number(id) : 0;

  const [activeTab, setActiveTab] = useState<Tab>(Tab.Subproductos);

  const {
    data: rawAssignations,
    isLoading,
    isError,
  } = useCreditLineAssignations(dispatch, requestId);

  if (isLoading) return <TabContentSkeleton />;

  if (isError)
    return (
      <ErrorState message="Ocurrió un error al cargar las concentraciones." />
    );

  const assignations = rawAssignations as
    | CreditLineAssignationsResponse
    | undefined;

  if (!assignations)
    return <ErrorState message="No se encontraron concentraciones." />;

  return (
    <Stack
      sx={{ position: 'relative', flex: 1, minHeight: '100%', px: 0 }}
      spacing={0}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <HeroHeader
          title="Concentraciones"
          minHeight={60}
          sx={{ width: '100vw', mx: 'calc(-50vw + 50%)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pt: 0.5,
            px: 2,
          }}
        >
          <BackBubbleButton onClick={() => navigate(-1)} />
        </Box>
      </Box>

      <Stack spacing={2} sx={{ px: 2, pt: 2 }}>
        <MobileTabs
          value={activeTab}
          items={TABS}
          onChange={(v) => setActiveTab(v as Tab)}
        />

        {activeTab === Tab.Deudores && (
          <>
            {assignations.stakeholderAssignations.length === 0 ? (
              <ErrorState message="No hay deudores asignados." />
            ) : (
              <ContentCard>
                <Stack spacing={0} direction="column">
                  {assignations.stakeholderAssignations.map((s) => (
                    <DocumentItem
                      key={s.businessIdentifier}
                      primaryText={s.businessName}
                      secondaryText={s.businessIdentifier}
                      amount={`${formatMoney(s.amount)} CLP`}
                    />
                  ))}
                </Stack>
              </ContentCard>
            )}
          </>
        )}

        {activeTab === Tab.Subproductos && (
          <>
            {assignations.subProductAssignations.length === 0 ? (
              <ErrorState message="No hay subproductos asignados." />
            ) : (
              assignations.subProductAssignations.map((sub) => (
                <SubProductCard key={sub.id} sub={sub} />
              ))
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default SubProducts;
